//dataflow.js
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Camera, Cpu, Image as ImageIcon, SlidersHorizontal, Save, Play,
  Eye, X, Check, RefreshCw, ZoomIn, ZoomOut, Maximize2,
  ChevronRight, Layers, Filter, ArrowRight, Database, Move,
  Wifi, CheckCircle2
} from "lucide-react";
import { Modal, Select, InputNumber, message, Spin } from "antd";
import axios from "../../utils/axios";
import { modeMap } from "../../constants/models";

/* ───────────────────────── constants ───────────────────────── */
const META = {
  camera:        { color: "#0284c7", label: "Camera",        Icon: Camera },
  usecase:       { color: "#7c3aed", label: "Model",         Icon: Cpu },
  frame:         { color: "#059669", label: "Frame Capture", Icon: ImageIcon },
  preprocessing: { color: "#d97706", label: "Preprocess",    Icon: Filter },
  augmentation:  { color: "#db2777", label: "Augment",       Icon: SlidersHorizontal },
};

// Singleton node types — only one of these allowed on canvas at a time
const SINGLETON_TYPES = new Set(["camera", "usecase", "frame"]);

const HEADER_GRAD = {
  camera:        "linear-gradient(135deg,#0284c7,#0369a1)",
  usecase:       "linear-gradient(135deg,#7c3aed,#6d28d9)",
  frame:         "linear-gradient(135deg,#059669,#047857)",
  preprocessing: "linear-gradient(135deg,#d97706,#b45309)",
  augmentation:  "linear-gradient(135deg,#db2777,#be185d)",
};

const PREPROCESS_OPTS = [
  { key: "resize",    label: "Image Resize",  cfg: { width: 512, height: 512 } },
  { key: "grayscale", label: "Grayscale",      cfg: {} },
  { key: "autoEdge",  label: "Edge Contrast",  cfg: { method: "contrast_stretching", fixedSize: 400, strength: 1.5, threshold: 0.1 } },
];

const AUGMENT_OPTS = [
  { key: "flip",       label: "Flip",        cfg: { horizontal: false, vertical: false } },
  { key: "rotation",   label: "Rotation",    cfg: { angle: 0 } },
  { key: "randomCrop", label: "Random Crop", cfg: { percentage: 80, direction: "random" } },
  { key: "blur",       label: "Blur",        cfg: { intensity: 0 } },
  { key: "brightness", label: "Brightness",  cfg: { value: 1 } },
  { key: "saturation", label: "Saturation",  cfg: { value: 1 } },
  { key: "exposure",   label: "Exposure",    cfg: { value: 1 } },
];

const ALL_PREPROCESS = {
  resize:    { label: "Image Resizing", width: 512, height: 512 },
  grayscale: { label: "Grayscale Conversion" },
  autoEdge:  {
    label: "Auto Edge Contrast",
    method: "contrast_stretching",
    fixedSize: 400,
    strength: 1.5,
    threshold: 0.1,
  },
};

const ALL_AUGMENT = {
  flip: {
    label: "Flip",
    horizontal: false,
    vertical: false,
    direction: "horizontal",
  },
  randomCrop: { label: "Random Crop", percentage: 80, direction: "random" },
  rotation:   { label: "Rotation", angle: 0 },
  blur:       { label: "Blur", intensity: 0 },
  saturation: { label: "Saturation", value: 1 },
  brightness: { label: "Brightness", value: 1 },
  exposure:   { label: "Exposure", value: 1 },
};

let _id = 0;
const uid = () => `n${++_id}_${Math.random().toString(36).slice(2, 6)}`;

/* ─────────────────────── Graph helpers ─────────────────────── */

function topologicalOrder(nodes, edges) {
  const inDegree = {};
  const adjList  = {};
  nodes.forEach(n => { inDegree[n.id] = 0; adjList[n.id] = []; });
  edges.forEach(e => {
    adjList[e.from]?.push(e.to);
    if (inDegree[e.to] !== undefined) inDegree[e.to]++;
  });

  const queue  = nodes.filter(n => inDegree[n.id] === 0).map(n => n.id);
  const result = [];
  while (queue.length) {
    const cur = queue.shift();
    result.push(cur);
    (adjList[cur] || []).forEach(next => {
      inDegree[next]--;
      if (inDegree[next] === 0) queue.push(next);
    });
  }
  return result;
}

function findUpstreamCamera(startId, nodes, edges) {
  const visited = new Set();
  const queue   = [startId];
  while (queue.length) {
    const cur = queue.shift();
    if (visited.has(cur)) continue;
    visited.add(cur);
    const n = nodes.find(x => x.id === cur);
    if (n?.type === "camera" && n.cameraId) return n.cameraId;
    edges.filter(e => e.to === cur).forEach(e => queue.push(e.from));
  }
  return null;
}

/* ──────────────────────── PaletteChip ──────────────────────── */
function PaletteChip({ type, label, subKey, cfg, disabled }) {
  const { color, Icon } = META[type];

  function handleDragStart(e) {
    if (disabled) { e.preventDefault(); return; }
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData(
      "application/x-dataflow",
      JSON.stringify({ type, label, subKey, cfg })
    );
  }

  return (
    <div
      draggable={!disabled}
      onDragStart={handleDragStart}
      className="flex items-center gap-2 px-3 py-2 rounded-lg select-none group transition-all"
      style={{
        background:  disabled ? "#f3f4f6"       : `${color}0d`,
        border:      `1px solid ${disabled ? "#e5e7eb" : color + "22"}`,
        cursor:      disabled ? "not-allowed"   : "grab",
        opacity:     disabled ? 0.45            : 1,
      }}
      onMouseEnter={e => {
        if (disabled) return;
        e.currentTarget.style.background    = `${color}18`;
        e.currentTarget.style.borderColor   = `${color}50`;
        e.currentTarget.style.boxShadow     = `0 2px 8px ${color}20`;
      }}
      onMouseLeave={e => {
        if (disabled) return;
        e.currentTarget.style.background    = `${color}0d`;
        e.currentTarget.style.borderColor   = `${color}22`;
        e.currentTarget.style.boxShadow     = "none";
      }}
    >
      <div className="w-7 h-7 rounded-md flex-shrink-0 flex items-center justify-center"
        style={{ background: `${color}18` }}>
        <Icon size={13} style={{ color: disabled ? "#9ca3af" : color }} />
      </div>
      <span className="text-xs font-semibold flex-1 truncate" style={{ color: disabled ? "#9ca3af" : "#374151" }}>
        {label}
      </span>
      {disabled
        ? <Check size={10} style={{ color: "#16a34a", flexShrink: 0 }} />
        : <Move size={9} className="text-gray-300 group-hover:text-gray-400 transition-colors flex-shrink-0" />
      }
    </div>
  );
}

/* ──────────────────────── CanvasNode ───────────────────────── */
function CanvasNode({ node, selected, onSelect, onDelete, onOutPortDown, onInPortUp, isWiring, children }) {
  const { color, Icon } = META[node.type];
  const dragRef = useRef({ active: false });

  function handleMouseDown(e) {
    if (e.target.closest("[data-nd]"))   return;
    if (e.target.closest("[data-port]")) return;
    if (isWiring) return;
    e.stopPropagation();
    onSelect(node.id);
    dragRef.current = { active: true, mx: e.clientX, my: e.clientY, nx: node.x, ny: node.y };

    function onMove(ev) {
      if (!dragRef.current.active) return;
      node._move(
        node.id,
        dragRef.current.nx + (ev.clientX - dragRef.current.mx),
        dragRef.current.ny + (ev.clientY - dragRef.current.my)
      );
    }
    function onUp() {
      dragRef.current.active = false;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  const w = node.w || 255;

  return (
    <div
      onMouseDown={handleMouseDown}
      className="absolute select-none"
      style={{ left: node.x, top: node.y, width: w, zIndex: selected ? 60 : 10, cursor: "grab" }}
    >
      {/* Card */}
      <div
        className="rounded-2xl overflow-visible"
        style={{
          background: "#ffffff",
          border: `1.5px solid ${selected ? color : color + "28"}`,
          boxShadow: selected
            ? `0 0 0 3px ${color}14, 0 8px 32px rgba(0,0,0,.12)`
            : "0 2px 12px rgba(0,0,0,.08), 0 1px 3px rgba(0,0,0,.06)",
          transition: "border-color .2s, box-shadow .2s",
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-t-2xl"
          style={{ background: HEADER_GRAD[node.type] }}>
          <div className="w-6 h-6 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
            <Icon size={12} className="text-white" />
          </div>
          <span className="text-white text-xs font-bold tracking-wide uppercase flex-1 truncate">
            {node.label}
          </span>
          {node.configured && <CheckCircle2 size={12} className="text-white/80 flex-shrink-0" />}
          <button
            data-nd
            onClick={e => { e.stopPropagation(); onDelete(node.id); }}
            className="w-5 h-5 rounded-full bg-white/20 hover:bg-red-400 flex items-center justify-center transition-colors flex-shrink-0"
          >
            <X size={9} className="text-white" />
          </button>
        </div>

        {/* Body */}
        <div className="p-3" data-nd>{children}</div>
      </div>

      {/* IN port — all except camera */}
      {node.type !== "camera" && (
        <div
          data-port
          onMouseUp={e => { e.stopPropagation(); onInPortUp(node.id); }}
          title="Input — drop connection here"
          className="absolute -left-3.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center z-20 cursor-crosshair border-2 transition-transform hover:scale-125"
          style={{ background: color, borderColor: "#ffffff", boxShadow: `0 2px 8px ${color}50` }}
        >
          <div className="w-2.5 h-2.5 rounded-full bg-white/60" />
        </div>
      )}

      {/* OUT port */}
      <div
        data-port
        onMouseDown={e => { e.stopPropagation(); onOutPortDown(e, node.id); }}
        title="Drag to connect"
        className="absolute -right-3.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center z-20 cursor-crosshair border-2 transition-transform hover:scale-125"
        style={{ background: color, borderColor: "#ffffff", boxShadow: `0 2px 8px ${color}50` }}
      >
        <ChevronRight size={11} className="text-white font-bold" />
      </div>

      {/* Status pill */}
      {node.status && (
        <div
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider"
          style={{
            background: node.status === "ready" ? "#16a34a" : node.status === "error" ? "#dc2626" : "#d97706",
            color: "white",
            boxShadow: "0 2px 6px rgba(0,0,0,.2)",
          }}
        >
          {/* FIX A: show "connect" hint on unconnected pre/aug nodes */}
          {node.status === "pending" && (node.type === "preprocessing" || node.type === "augmentation")
            ? "connect"
            : node.status}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── BezierEdge ────────────────────────── */
function BezierEdge({ id, x1, y1, x2, y2, color, onDelete }) {
  const [hov, setHov] = useState(false);
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const d  = `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;

  return (
    <g onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>
      {/* fat invisible hit area */}
      <path d={d} fill="none" stroke="transparent" strokeWidth={18} style={{ cursor: "pointer" }} />
      {/* glow */}
      <path d={d} fill="none" stroke={color} strokeWidth={hov ? 5 : 3.5} opacity={.08} />
      {/* animated dash */}
      <path
        d={d} fill="none" stroke={color}
        strokeWidth={hov ? 2.5 : 1.8}
        strokeDasharray="10 5"
        opacity={hov ? 1 : .5}
        style={{ animation: "dashMove 1.1s linear infinite" }}
      />
      <circle cx={x2} cy={y2} r={4.5} fill={color} opacity={.9} />

      {/* DELETE button */}
      {hov && (
        <g
          onClick={e => { e.stopPropagation(); onDelete(id); }}
          style={{ cursor: "pointer" }}
        >
          <circle cx={mx} cy={my} r={11} fill="white" stroke={color} strokeWidth={1.5} />
          <text x={mx} y={my + 5} textAnchor="middle" fontSize={15} fontWeight="bold" fill={color}>×</text>
        </g>
      )}
    </g>
  );
}

/* ────────────────────── NodeBody renderers ──────────────────── */
function CameraBody({ node, cameras, updateNode }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] text-gray-400 mb-1 font-semibold uppercase tracking-wider">Select Camera</p>
      <Select
        data-nd
        placeholder="Choose a camera…"
        value={node.cameraId ?? undefined}
        onChange={val => {
          const cam = cameras.find(c => c.channel === val);
          updateNode(node.id, {
            cameraId: val,
            label: cam?.cameraName || "Camera",
            configured: true,
            status: "ready",
          });
        }}
        size="small"
        className="w-full"
      >
        {cameras.map(c => (
          <Select.Option key={c.channel} value={c.channel}>
            <span className="text-xs">{c.cameraName} — Ch {c.channel}</span>
          </Select.Option>
        ))}
      </Select>
      {node.cameraId && (
        <div className="flex items-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded-lg"
          style={{ background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" }}>
          <Wifi size={10} /> Connected · Channel {node.cameraId}
        </div>
      )}
    </div>
  );
}

function UsecaseBody({ node, allModels, updateNode }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Assign Models</p>
      <Select
        data-nd
        mode="multiple"
        placeholder="Select models…"
        size="small"
        className="w-full"
        value={(node.usecases || []).map(m => `${m.name}__${m.modelType}`)}
        onChange={values => {
          const parsed = values.map(v => {
            const [name, modelType] = v.split("__");
            return { name, modelType };
          });
          updateNode(node.id, {
            usecases: parsed,
            configured: parsed.length > 0,
            status: parsed.length ? "ready" : "pending",
          });
        }}
      >
        {allModels.map(m => (
          <Select.Option key={m.value} value={m.value}>
            <div className="flex justify-between items-center">
              <span>{m.label}</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                style={{
                  background: m.modelType === "pretrained" ? "#eff6ff" : "#f5f3ff",
                  color:      m.modelType === "pretrained" ? "#2563eb" : "#7c3aed",
                  border: "1px solid #e5e7eb",
                }}>
                {m.modelType === "pretrained" ? "Pre-trained" : "Custom"}
              </span>
            </div>
          </Select.Option>
        ))}
      </Select>
    </div>
  );
}

function FrameBody({ node, captureFrame, capLoading, setPreviewSrc }) {
  return (
    <div className="space-y-2">
      {node.frameData ? (
        <div className="relative group rounded-xl overflow-hidden" style={{ border: "1px solid #d1fae5" }}>
          <img src={node.frameData} alt="frame" className="w-full h-28 object-cover" />
          <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
            <button data-nd onClick={() => setPreviewSrc(node.frameData)}
              className="p-2 rounded-full bg-white/25 hover:bg-white/50 transition">
              <Eye size={13} className="text-white" />
            </button>
            <button data-nd onClick={() => captureFrame(node.id)}
              className="p-2 rounded-full bg-white/25 hover:bg-white/50 transition">
              <RefreshCw size={13} className="text-white" />
            </button>
          </div>
        </div>
      ) : (
        <button
          data-nd
          disabled={capLoading === node.id}
          onClick={() => captureFrame(node.id)}
          className="w-full py-5 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider transition-all"
          style={{ borderColor: "#6ee7b7", color: "#059669", background: "#f0fdf4" }}
          onMouseEnter={e => { e.currentTarget.style.background = "#dcfce7"; e.currentTarget.style.borderColor = "#34d399"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#f0fdf4"; e.currentTarget.style.borderColor = "#6ee7b7"; }}
        >
          {capLoading === node.id
            ? <><Spin size="small" /><span className="ml-1">Capturing…</span></>
            : <><Camera size={14} /><span>Capture Frame</span></>}
        </button>
      )}
    </div>
  );
}

/* FIX B — toggle now properly sets configured + status */
function PreprocessBody({ node, updateNode }) {
  const cfg   = node.config || {};
  const on    = node.enabled;
  const color = "#d97706";

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-gray-500 font-semibold">{node.label}</span>
        <button
          data-nd
          onClick={() => updateNode(node.id, {
            enabled:    !on,
            configured: !on,       // ON → configured true, OFF → false
            status:     "pending", // stays pending until wired (edge effect sets "ready")
          })}
          className="text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider transition-all"
          style={{ background: on ? "#fef3c7" : "#f3f4f6", color: on ? color : "#9ca3af", border: `1px solid ${on ? "#fde68a" : "#e5e7eb"}` }}>
          {on ? "ON" : "OFF"}
        </button>
      </div>
      {on && (
        <>
          {node.subKey === "resize" && (
            <div className="grid grid-cols-2 gap-1.5">
              {["width", "height"].map(k => (
                <div key={k}>
                  <p className="text-[9px] text-gray-400 mb-0.5 capitalize">{k}</p>
                  <InputNumber data-nd size="small" min={32} max={2048} value={cfg[k]}
                    onChange={v => updateNode(node.id, { config: { ...cfg, [k]: v } })} className="w-full" />
                </div>
              ))}
            </div>
          )}
          {node.subKey === "autoEdge" && (
            <Select data-nd size="small" value={cfg.method}
              onChange={v => updateNode(node.id, { config: { ...cfg, method: v } })} className="w-full">
              <Select.Option value="contrast_stretching">Contrast Stretching</Select.Option>
              <Select.Option value="histogram_equalization">Histogram Equalization</Select.Option>
              <Select.Option value="adaptive_equalization">CLAHE</Select.Option>
            </Select>
          )}
          {node.subKey === "grayscale" && (
            <p className="text-[10px] text-gray-400">Converts image channels to grayscale.</p>
          )}
        </>
      )}
    </div>
  );
}

/* FIX B — toggle now properly sets configured + status */
function AugmentBody({ node, updateNode }) {
  const cfg   = node.config || {};
  const on    = node.enabled;
  const color = "#db2777";

  const Slider = ({ label, field, min, max, step = 1, unit = "" }) => (
    <div>
      <div className="flex justify-between text-[10px] mb-1">
        <span className="text-gray-400">{label}</span>
        <span className="font-bold" style={{ color }}>{cfg[field]}{unit}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={cfg[field] ?? min}
        data-nd
        onChange={e => updateNode(node.id, { config: { ...cfg, [field]: +e.target.value } })}
        className="w-full" style={{ accentColor: color }} />
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-gray-500 font-semibold">{node.label}</span>
        <button
          data-nd
          onClick={() => updateNode(node.id, {
            enabled:    !on,
            configured: !on,       // ON → configured true, OFF → false
            status:     "pending", // stays pending until wired (edge effect sets "ready")
          })}
          className="text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider transition-all"
          style={{ background: on ? "#fdf2f8" : "#f3f4f6", color: on ? color : "#9ca3af", border: `1px solid ${on ? "#fbcfe8" : "#e5e7eb"}` }}>
          {on ? "ON" : "OFF"}
        </button>
      </div>
      {on && (
        <>
          {node.subKey === "flip" && (
            <div className="flex gap-1.5">
              {["horizontal", "vertical"].map(d => (
                <button key={d} data-nd
                  onClick={() => updateNode(node.id, { config: { ...cfg, [d]: !cfg[d] } })}
                  className="flex-1 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all"
                  style={{
                    background: cfg[d] ? color : "#f9fafb",
                    color: cfg[d] ? "white" : "#9ca3af",
                    border: `1px solid ${cfg[d] ? color : "#e5e7eb"}`,
                  }}>
                  {d[0].toUpperCase() + d.slice(1)}
                </button>
              ))}
            </div>
          )}
          {node.subKey === "rotation"   && <Slider label="Angle"      field="angle"      min={0} max={360} unit="°" />}
          {node.subKey === "blur"       && <Slider label="Intensity"   field="intensity"  min={0} max={20}  unit="px" />}
          {["brightness", "saturation", "exposure"].includes(node.subKey) &&
            <Slider label="Level" field="value" min={0} max={3} step={0.05} unit="×" />}
          {node.subKey === "randomCrop" && (
            <div className="space-y-1.5">
              <Slider label="Crop Size" field="percentage" min={10} max={100} unit="%" />
              <Select data-nd size="small" value={cfg.direction}
                onChange={v => updateNode(node.id, { config: { ...cfg, direction: v } })} className="w-full">
                {["random", "left", "right", "top", "bottom", "center"].map(d =>
                  <Select.Option key={d} value={d}>{d[0].toUpperCase() + d.slice(1)}</Select.Option>)}
              </Select>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ──────────────────────── Main Component ────────────────────── */
export default function DataflowWorkflow() {
  const [nodes, setNodes]       = useState([]);
  const [edges, setEdges]       = useState([]);
  const [selected, setSelected] = useState(null);

  const [pan, setPan]   = useState({ x: 120, y: 80 });
  const [zoom, setZoom] = useState(1);
  const panActive       = useRef(false);
  const panOrigin       = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  const [wire, setWire]   = useState(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const [cameras, setCameras]       = useState([]);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [capLoading, setCapLoading] = useState(null);
  const [outputSteps, setOutputSteps] = useState([]);
  const [showOutput, setShowOutput] = useState(false);

  const [previewSrc, setPreviewSrc]       = useState(null);
  const [confirmNodeId, setConfirmNodeId] = useState(null);
  const [customModels, setCustomModels]   = useState([]);
  const [pretrainedModels, setPretrainedModels] = useState([]);
  const [modelOutputImage, setModelOutputImage] = useState(null);

  const panRef  = useRef({ x: 120, y: 80 });
  const zoomRef = useRef(1);
  const wrapRef = useRef(null);

  const projectname    = localStorage.getItem("projectname");
  const organizationId = localStorage.getItem("organizationId");
  const user           = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => { panRef.current  = pan;  }, [pan]);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.post("/fetch-cameras", { organizationId, projectname });
        if (r.data.success) setCameras(r.data.cameras || []);
      } catch { message.error("Failed to load cameras"); }
    };
    load();
  }, []);

  useEffect(() => {
    fetchTrainedModels();
  }, []);

  const moveNode   = useCallback((id, x, y) =>
    setNodes(ns => ns.map(n => n.id === id ? { ...n, x, y } : n)), []);

  const updateNode = useCallback((id, patch) =>
    setNodes(ns => ns.map(n => n.id === id ? { ...n, ...patch } : n)), []);

  const deleteNode = useCallback((id) => {
    setNodes(ns => ns.filter(n => n.id !== id));
    setEdges(es => es.filter(e => e.from !== id && e.to !== id));
    setSelected(s => s === id ? null : s);
  }, []);

  const deleteEdge = useCallback((id) =>
    setEdges(es => es.filter(e => e.id !== id)), []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/x-dataflow");
    if (!raw) return;

    let data;
    try { data = JSON.parse(raw); } catch { return; }

    if (SINGLETON_TYPES.has(data.type)) {
      if (nodes.some(n => n.type === data.type)) {
        message.warning(`A ${META[data.type].label} node is already on the canvas.`);
        return;
      }
    }

    if ((data.type === "preprocessing" || data.type === "augmentation") && data.subKey) {
      if (nodes.some(n => n.type === data.type && n.subKey === data.subKey)) {
        message.warning(`A "${data.label}" node is already on the canvas.`);
        return;
      }
    }

    const target = e.currentTarget;
    const rect   = target.getBoundingClientRect();
    const p      = panRef.current;
    const z      = zoomRef.current;

    const x = (e.clientX - rect.left - p.x) / z;
    const y = (e.clientY - rect.top  - p.y) / z;

    const id   = uid();
    // const base = { id, x, y, type: data.type, label: data.label, configured: false, status: "pending", _move: moveNode };
    const base = { 
      id, x, y, type: data.type, label: data.label, 
      configured: false, status: "pending"
    };

    let node = { ...base };
    if (data.type === "camera")       node = { ...base, w: 270, cameraId: null };
    else if (data.type === "usecase") node = { ...base, w: 280, usecases: [] };
    else if (data.type === "frame")   node = { ...base, w: 265, frameData: null };
    else {
      /* FIX C — drop as enabled+configured but status stays "pending"
         until wired. The edge-sync effect below promotes it to "ready". */
      node = {
        ...base, w: 258,
        subKey:     data.subKey,
        config:     { ...(data.cfg || {}) },
        enabled:    true,
        configured: true,
        status:     "pending",
      };
    }

    setNodes(ns => [...ns, node]);
    setSelected(id);
  }, [nodes, moveNode]);

  /* FIX C (cont.) — whenever edges change, sync status of all pre/aug nodes.
     A node is "ready" only when it is enabled AND has at least one edge.     */
  useEffect(() => {
    setNodes(ns => ns.map(n => {
      if (n.type !== "preprocessing" && n.type !== "augmentation") return n;
      const connected = edges.some(e => e.from === n.id || e.to === n.id);
      return { ...n, status: (n.enabled && connected) ? "ready" : "pending" };
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edges]);

  const onCanvasMouseDown = (e) => {
    if (!e.target.classList.contains("canvas-bg-layer")) return;
    setSelected(null);
    setWire(null);
    panActive.current = true;
    panOrigin.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
  };

  useEffect(() => {
    const onMove = (e) => {
      if (panActive.current) {
        setPan({
          x: panOrigin.current.px + (e.clientX - panOrigin.current.mx),
          y: panOrigin.current.py + (e.clientY - panOrigin.current.my),
        });
      }
      if (wire && wrapRef.current) {
        const rect = wrapRef.current.getBoundingClientRect();
        setMouse({
          x: (e.clientX - rect.left - panRef.current.x) / zoomRef.current,
          y: (e.clientY - rect.top  - panRef.current.y) / zoomRef.current,
        });
      }
    };
    const onUp = () => { panActive.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [wire]);

  const onCanvasMouseUp = (e) => {
    if (wire) setWire(null);
  };

  const onWheel = (e) => {
  // If scrolling inside any interactive element → ignore zoom
    if (
      e.target.closest(".ant-select-dropdown") ||
      e.target.closest(".ant-select") ||
      e.target.closest(".ant-modal") ||
      e.target.closest("[data-nd]") // node content
    ) {
      return;
    }

    e.preventDefault();

    setZoom(z =>
      Math.max(0.25, Math.min(2.5, z * (e.deltaY < 0 ? 1.08 : 0.93)))
    );
  };

  const onOutPortDown = useCallback((e, fromId) => {
    e.stopPropagation();
    const n = nodes.find(x => x.id === fromId);
    if (!n) return;
    setWire({ fromId });
    setMouse({ x: n.x + (n.w || 255), y: n.y + 60 });
  }, [nodes]);

  const onInPortUp = useCallback((toId) => {
    if (!wire || wire.fromId === toId) { setWire(null); return; }
    setEdges(es => {
      const filtered = es.filter(e => e.from !== wire.fromId && e.to !== toId);
      return [...filtered, { id: uid(), from: wire.fromId, to: toId }];
    });
    setWire(null);
  }, [wire]);

  const outPt = (n) => ({ x: n.x + (n.w || 255), y: n.y + 60 });
  const inPt  = (n) => ({ x: n.x,                 y: n.y + 60 });

  const captureFrame = async (nodeId) => {
    let cameraId = findUpstreamCamera(nodeId, nodes, edges);
    if (!cameraId) {
      const anyCam = nodes.find(n => n.type === "camera" && n.cameraId);
      if (anyCam) cameraId = anyCam.cameraId;
    }
    if (!cameraId) {
      message.warning("Add and configure a Camera node first (connection not required)");
      return;
    }

    setCapLoading(nodeId);
    try {
      const res = await axios.post("/pre-trained-preview-frame", { projectname, cameraId });
      if (res?.data?.success) {
        updateNode(nodeId, { frameData: res.data.previewBase64, configured: true, status: "ready" });
        setPreviewSrc(res.data.previewBase64);
        setConfirmNodeId(nodeId);
        message.success("Frame captured!");
      } else {
        message.error("Failed to capture frame");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Capture error");
    } finally {
      setCapLoading(null);
    }
  };

  const applyTransformations = async () => {
    const frameNode = nodes.find(n => n.type === "frame" && n.frameData);
    if (!frameNode) { message.warning("Capture a frame first"); return; }

    const orderedIds = topologicalOrder(nodes, edges);
    const txNodes = orderedIds
      .map(id => nodes.find(n => n.id === id))
      .filter(n => n && (n.type === "preprocessing" || n.type === "augmentation") && n.enabled);

    const ucNode   = nodes.find(n => n.type === "usecase");
    const usecases = ucNode?.usecases || [];

    const pipeline = [];
    txNodes.forEach(n => {
      // const cfg = Object.fromEntries(
      //   Object.entries(n.config || {}).filter(([k]) => !["enabled", "label"].includes(k))
      // );
      const cfg = Object.fromEntries(
  Object.entries(n.config || {}).filter(([k, v]) => {
    return (
      !["enabled", "label"].includes(k) &&
      typeof v !== "function" &&
      !(v instanceof Element) &&
      !(v?.target instanceof Element)
    );
  })
);
      if (n.subKey === "flip") {
        if (cfg.horizontal) pipeline.push({ type: "flip(horizontal)", config: { direction: "horizontal" } });
        if (cfg.vertical)   pipeline.push({ type: "flip(vertical)",   config: { direction: "vertical"   } });
        return;
      }
      pipeline.push({ type: n.subKey, config: cfg });
    });

    setProcessing(true);
    try {
      const res = await axios.post("/preview-transform", {
        imageBase64: frameNode.frameData,
        pipeline,
        usecases,
      });
      if (res.data.success) {
        setOutputSteps(res.data.steps || []);
        setShowOutput(true);
        setModelOutputImage(res.data.pythonImage || null);
        message.success(`${res.data.steps?.length} transformations applied!`);
      } else {
        message.error("Transformation failed");
      }
    } catch {
      message.error("Server error");
    } finally {
      setProcessing(false);
    }
  };

  const savePipeline = async (cameraIds = null) => {
    const camNode = nodes.find(n => n.type === "camera" && n.cameraId);
    const ucNode  = nodes.find(n => n.type === "usecase" && n.usecases?.length);
    if (!camNode) { message.warning("Configure a Camera node"); return; }
    if (!ucNode)  { message.warning("Configure a Use Case node"); return; }
    if (!outputSteps.length) { message.warning("Apply transformations first"); return; }
    if (cameraIds === null) {
      const otherCameras = cameras.filter(c => c.channel !== camNode.cameraId);
      if (otherCameras.length > 0) {
        setShowCameraModal(true);
        return;
      }
      cameraIds = [camNode.cameraId];
    }

    const orderedIds = topologicalOrder(nodes, edges);
    const preprocessing = {};
    const augmentation  = {};

    Object.entries(ALL_PREPROCESS).forEach(([key, defaults]) => {
      const node = nodes.find(n => n.type === "preprocessing" && n.subKey === key);
      preprocessing[key] = { enabled: !!node?.enabled, label: defaults.label, ...(node?.config || defaults) };
    });

    Object.entries(ALL_AUGMENT).forEach(([key, defaults]) => {
      const node = nodes.find(n => n.type === "augmentation" && n.subKey === key);
      augmentation[key] = { enabled: !!node?.enabled, label: defaults.label, ...(node?.config || defaults) };
    });

    const txNodes = orderedIds
      .map(id => nodes.find(n => n.id === id))
      .filter(n => n && (n.type === "preprocessing" || n.type === "augmentation") && n.enabled);

    const pipeline = [];
    txNodes.forEach(n => {
      const cfg = Object.fromEntries(
        Object.entries(n.config || {}).filter(([k, v]) => {
          return (
            !["enabled", "label"].includes(k) &&
            typeof v !== "function" &&
            !(v instanceof Element) &&
            !(v?.target instanceof Element)
          );
        })
      );
      if (n.subKey === "flip") {
        if (cfg.horizontal) pipeline.push({ type: "flip(horizontal)", config: { direction: "horizontal" } });
        if (cfg.vertical)   pipeline.push({ type: "flip(vertical)",   config: { direction: "vertical"   } });
        return;
      }
      pipeline.push({ type: n.subKey, config: cfg });
    });

    setSaving(true);
    try {
      const safePipeline = JSON.parse(JSON.stringify(pipeline));
      const safePreprocessing = JSON.parse(JSON.stringify(preprocessing));
      const safeAugmentation = JSON.parse(JSON.stringify(augmentation));
      const safeUsecase = JSON.parse(JSON.stringify(ucNode.usecases));
      const res = await axios.post("/savedataflow-pipeline", {
        username: user.username,
        projectname,
        organizationId,
        cameraIds,
        usecase: safeUsecase,
        preprocessing: safePreprocessing,
        augmentation: safeAugmentation,
        pipeline: safePipeline,
      });
      if (res.data.success) {
        message.success(
          cameraIds.length > 1
            ? `Pipeline saved for ${cameraIds.length} cameras!`
            : "Pipeline saved!"
        );
      } else {
        message.error("Save failed");
      }
    } catch (err) {
      message.error("Save request failed");
    } finally {
      setSaving(false);
      setShowCameraModal(false);
    }
  };

  const hasCam   = nodes.some(n => n.type === "camera"  && n.cameraId);
  const hasUC    = nodes.some(n => n.type === "usecase" && n.usecases?.length);
  const hasFrame = nodes.some(n => n.type === "frame"   && n.frameData);
  const hasTx    = nodes.some(n => (n.type === "preprocessing" || n.type === "augmentation") && n.enabled);

  /* FIX D — Apply is blocked if any enabled pre/aug node has no edge.
     If there are no tx nodes at all, this passes (tx optional).          */
  const enabledTxNodes = nodes.filter(
    n => (n.type === "preprocessing" || n.type === "augmentation") && n.enabled
  );
  const allTxConnected = enabledTxNodes.length === 0 ||
    enabledTxNodes.every(n => edges.some(e => e.from === n.id || e.to === n.id));

  const canApply = hasFrame && hasUC && allTxConnected;
  const canSave  = canApply && outputSteps.length > 0 && hasCam && hasUC;

  const STEPS = [
    { label: "Add & configure camera",       done: hasCam },
    { label: "Add use case node",             done: hasUC },
    { label: "Capture a preview frame",       done: hasFrame },
    { label: "Add & connect transform nodes", done: hasTx && allTxConnected },
    { label: "Apply, then save",              done: outputSteps.length > 0 },
  ];

  const presentTypes = new Set(nodes.map(n => n.type));

  const renderBody = (node) => {
    if (node.type === "camera")        return <CameraBody node={node} cameras={cameras} updateNode={updateNode} />;
    if (node.type === "usecase")       return <UsecaseBody node={node} allModels={allModels} updateNode={updateNode} />;
    if (node.type === "frame")         return (
      <FrameBody node={node} captureFrame={captureFrame} capLoading={capLoading} setPreviewSrc={setPreviewSrc} />
    );
    if (node.type === "preprocessing") return <PreprocessBody node={node} updateNode={updateNode} />;
    if (node.type === "augmentation")  return <AugmentBody    node={node} updateNode={updateNode} />;
    return null;
  };

  const fetchTrainedModels = async () => {
    try {
      const res = await axios.post("/fetch-trained-models", { projectname, organizationId });
      if (res?.data?.models?.length > 0) {
        setCustomModels(res.data.models.map(m => ({ name: m.useCase, type: "custom" })));
      }
    } catch (err) {
      if (err.response?.status === 401) message.error("User not authorised");
      else message.error("Something went wrong");
    }
  };

  useEffect(() => {
  setPretrainedModels(
    Object.keys(modeMap).map(key => ({
      name: key,
      type: "pretrained"
    }))
  );
}, []);


  const allModels = React.useMemo(() => [
    ...pretrainedModels.map(m => ({ label: m.name, value: `${m.name}__pretrained`, modelType: "pretrained" })),
    ...customModels.map(m =>     ({ label: m.name, value: `${m.name}__custom`,     modelType: "custom"     })),
  ], [pretrainedModels, customModels]);

  return (
    <div className="flex h-screen overflow-hidden"
      style={{ background: "#f8fafc", fontFamily: "'DM Mono','Fira Code',monospace" }}>
      <style>{`
        @keyframes dashMove { to { stroke-dashoffset: -30; } }
        @keyframes popIn { from { opacity:0; transform:scale(.85); } to { opacity:1; transform:scale(1); } }
        .node-pop { animation: popIn 0.2s cubic-bezier(.34,1.56,.64,1); }
        ::-webkit-scrollbar { width:4px; } ::-webkit-scrollbar-thumb { background:#e2e8f0; border-radius:4px; }
        .ant-select-selector { background:#ffffff !important; border-color:#e5e7eb !important; color:#374151 !important; }
        .ant-select-dropdown { background:#ffffff !important; border:1px solid #e5e7eb !important; box-shadow:0 4px 16px rgba(0,0,0,.1) !important; }
        .ant-select-item { color:#374151 !important; }
        .ant-select-item-option-active  { background:#f3f4f6 !important; }
        .ant-select-item-option-selected{ background:#eff6ff !important; color:#2563eb !important; }
        .ant-input-number { background:#ffffff !important; border-color:#e5e7eb !important; }
        .ant-input-number-input { color:#374151 !important; background:transparent !important; }
        .ant-input-number-handler-wrap { background:#f3f4f6 !important; }
        .ant-select-selection-placeholder { color:#9ca3af !important; }
        .ant-select-selection-item { color:#374151 !important; }
        .ant-select-arrow { color:#9ca3af !important; }
      `}</style>

      {/* ── LEFT PANEL ─── */}
      <aside className="w-64 flex-shrink-0 flex flex-col border-r"
        style={{ background: "#ffffff", borderColor: "#e5e7eb" }}>

        <div className="px-4 py-4 border-b" style={{ borderColor: "#e5e7eb" }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg,#0284c7,#7c3aed)" }}>
              <Database size={16} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-sm leading-none tracking-tight" style={{ color: "#111827" }}>Dataflow Builder</p>
              <p className="text-[9px] uppercase tracking-widest mt-0.5" style={{ color: "#9ca3af" }}>Visual Pipeline Builder</p>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="px-4 py-2 border-b" style={{ borderColor: "#e5e7eb" }}>
          <p className="text-[9px] font-bold uppercase tracking-widest mb-2.5" style={{ color: "#9ca3af" }}>Steps</p>
          <div className="space-y-2">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: s.done ? "#16a34a" : "#f3f4f6", border: `1px solid ${s.done ? "#16a34a" : "#d1d5db"}` }}>
                  {s.done
                    ? <Check size={8} className="text-white" />
                    : <span className="text-[7px] font-bold" style={{ color: "#9ca3af" }}>{i + 1}</span>}
                </div>
                <span className="text-[10px]" style={{ color: s.done ? "#16a34a" : "#6b7280" }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Palette */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          <section>
            <p className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{ color: "#9ca3af" }}>Core Nodes</p>
            <div className="space-y-1.5">
              <PaletteChip type="camera"  label="Camera"        disabled={presentTypes.has("camera")} />
              <PaletteChip type="usecase" label="Model"         disabled={presentTypes.has("usecase")} />
              <PaletteChip type="frame"   label="Frame Capture" disabled={presentTypes.has("frame")} />
            </div>
          </section>

          <section>
            <p className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{ color: "#9ca3af" }}>Preprocessing</p>
            <div className="space-y-1.5">
              {PREPROCESS_OPTS.map(o => (
                <PaletteChip key={o.key} type="preprocessing" label={o.label} subKey={o.key} cfg={o.cfg}
                  disabled={nodes.some(n => n.type === "preprocessing" && n.subKey === o.key)} />
              ))}
            </div>
          </section>

          <section>
            <p className="text-[9px] font-bold uppercase tracking-widest mb-2" style={{ color: "#9ca3af" }}>Augmentation</p>
            <div className="space-y-1.5">
              {AUGMENT_OPTS.map(o => (
                <PaletteChip key={o.key} type="augmentation" label={o.label} subKey={o.key} cfg={o.cfg}
                  disabled={nodes.some(n => n.type === "augmentation" && n.subKey === o.key)} />
              ))}
            </div>
          </section>
        </div>

        {/* Hint */}
        <div className="px-4 py-3 border-t" style={{ borderColor: "#e5e7eb" }}>
          <div className="rounded-xl p-2.5" style={{ background: "#f8fafc", border: "1px solid #e5e7eb" }}>
            <p className="text-[9px] font-bold uppercase tracking-wider mb-1.5" style={{ color: "#9ca3af" }}>Connections</p>
            {[
              "Drag node from palette → canvas",
              "Each node type can only be placed once",
              "Drag ▶ right port to start a wire",
              "Release on ● left port to connect",
              "Connect all nodes to enable Apply",
              "Hover any wire → click × to delete",
            ].map((t, i) => (
              <p key={i} className="text-[9px] flex gap-1.5 mb-0.5" style={{ color: "#9ca3af" }}>
                <span style={{ color: "#0284c7" }}>›</span>{t}
              </p>
            ))}
          </div>
        </div>
      </aside>

      {/* ── MAIN ─── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Toolbar */}
        <div className="h-12 flex items-center gap-3 px-4 flex-shrink-0 border-b"
          style={{ background: "#ffffff", borderColor: "#e5e7eb" }}>
          <div className="flex items-center gap-0.5 rounded-lg p-0.5" style={{ background: "#f3f4f6" }}>
            {[
              { Icon: ZoomIn,    act: () => setZoom(z => Math.min(2.5, z + 0.1)) },
              { Icon: ZoomOut,   act: () => setZoom(z => Math.max(0.25, z - 0.1)) },
              { Icon: Maximize2, act: () => { setZoom(1); setPan({ x: 120, y: 80 }); } },
            ].map(({ Icon, act }, i) => (
              <button key={i} onClick={act}
                className="w-7 h-7 rounded-md flex items-center justify-center transition-colors hover:bg-white"
                style={{ color: "#6b7280" }}
                onMouseEnter={e => e.currentTarget.style.color = "#111827"}
                onMouseLeave={e => e.currentTarget.style.color = "#6b7280"}>
                <Icon size={13} />
              </button>
            ))}
            <span className="text-[10px] font-mono w-10 text-center" style={{ color: "#6b7280" }}>
              {Math.round(zoom * 100)}%
            </span>
          </div>

          {wire && (
            <div className="flex items-center gap-2 px-2.5 py-1 rounded-lg text-xs"
              style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#2563eb" }}>
              <span className="animate-pulse">●</span>
              <span>Drag to an input port (●) to connect</span>
              <button onClick={() => setWire(null)}><X size={11} /></button>
            </div>
          )}

          {/* Warning banner: tx nodes present but not all wired yet */}
          {hasTx && !allTxConnected && !wire && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs"
              style={{ background: "#fff7ed", border: "1px solid #fed7aa", color: "#c2410c" }}>
              <span>⚠</span>
              <span>Connect all transform nodes to enable Apply</span>
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            <span className="text-[10px] font-mono" style={{ color: "#9ca3af" }}>
              {nodes.length} nodes · {edges.length} edges
            </span>

            {outputSteps.length > 0 && (
              <button onClick={() => setShowOutput(v => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#16a34a" }}>
                <Layers size={12} /> Output ({outputSteps.length})
              </button>
            )}

            <button onClick={applyTransformations} disabled={!canApply || processing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
              style={{
                background: canApply && !processing ? "linear-gradient(135deg,#4f46e5,#7c3aed)" : "#f3f4f6",
                color:  canApply && !processing ? "white" : "#d1d5db",
                boxShadow: canApply && !processing ? "0 2px 8px #4f46e530" : "none",
                cursor: canApply && !processing ? "pointer" : "not-allowed",
                border: "none",
              }}>
              {processing
                ? <><RefreshCw size={11} className="animate-spin" /> Processing…</>
                : <><Play size={11} /> Apply</>}
            </button>

            {/* <button onClick={() => savePipeline()} disabled={!canSave || saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
              style={{
                background: canSave && !saving ? "linear-gradient(135deg,#059669,#16a34a)" : "#f3f4f6",
                color:  canSave && !saving ? "white" : "#d1d5db",
                boxShadow: canSave && !saving ? "0 2px 8px #05966930" : "none",
                cursor: canSave && !saving ? "pointer" : "not-allowed",
                border: "none",
              }}>
              {saving
                ? <><RefreshCw size={11} className="animate-spin" /> Saving…</>
                : <><Save size={11} /> Save</>}
            </button> */}
          </div>
        </div>

        {/* Canvas */}
        <div
          ref={wrapRef}
          className="flex-1 relative overflow-hidden"
          onDrop={onDrop}
          onDragOver={e => e.preventDefault()}
          onMouseDown={onCanvasMouseDown}
          onMouseUp={onCanvasMouseUp}
          onWheel={onWheel}
          style={{ cursor: panActive.current ? "grabbing" : wire ? "crosshair" : "default" }}
        >
          {/* Dot-grid background */}
          <div className="canvas-bg-layer absolute inset-0 pointer-events-none"
            style={{
              backgroundColor: "#f8fafc",
              backgroundImage: "radial-gradient(circle, #cbd5e1 1px, transparent 1px)",
              backgroundSize: `${zoom * 24}px ${zoom * 24}px`,
              backgroundPosition: `${pan.x}px ${pan.y}px`,
            }}
          />
          <div className="canvas-bg-layer absolute inset-0" style={{ zIndex: 1 }} />

          {/* Empty state */}
          {nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 2 }}>
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-5 rounded-3xl flex items-center justify-center"
                  style={{ background: "#f1f5f9", border: "2px dashed #cbd5e1" }}>
                  <ArrowRight size={28} style={{ color: "#cbd5e1" }} />
                </div>
                <p className="font-bold text-lg tracking-tight" style={{ color: "#94a3b8" }}>Build your pipeline</p>
                <p className="text-sm mt-1" style={{ color: "#cbd5e1" }}>Drag nodes from the left panel onto the canvas</p>
                <div className="flex items-center justify-center gap-2 mt-5 flex-wrap">
                  {["Camera", "→", "Model", "→", "Frame", "→", "Transform", "→", "Save"].map((t, i) => (
                    <span key={i} className="text-[10px] px-2 py-1 rounded-md font-mono"
                      style={{
                        background: t === "→" ? "transparent" : "#f1f5f9",
                        color:      t === "→" ? "#cbd5e1"     : "#94a3b8",
                        border:     t === "→" ? "none"        : "1px solid #e2e8f0",
                      }}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Nodes layer */}
          <div className="absolute inset-0"
            style={{ transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom})`, transformOrigin: "0 0", zIndex: 10 }}>
            {nodes.map(node => (
              <div key={node.id} className="node-pop">
                <CanvasNode
                  node={{ ...node, _move: moveNode }}
                  selected={selected === node.id}
                  onSelect={setSelected}
                  onDelete={deleteNode}
                  onOutPortDown={onOutPortDown}
                  onInPortUp={onInPortUp}
                  isWiring={!!wire}
                >
                  {renderBody(node)}
                </CanvasNode>
              </div>
            ))}
          </div>

          {/* SVG edges layer */}
          <svg className="absolute inset-0 w-full h-full"
            style={{ zIndex: 20, pointerEvents: "none" }}>
            <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}
              style={{ pointerEvents: "all" }}>

              {edges.map(edge => {
                const fn = nodes.find(n => n.id === edge.from);
                const tn = nodes.find(n => n.id === edge.to);
                if (!fn || !tn) return null;
                const op = outPt(fn), ip = inPt(tn);
                return (
                  <BezierEdge key={edge.id} id={edge.id}
                    x1={op.x} y1={op.y} x2={ip.x} y2={ip.y}
                    color={META[fn.type]?.color || "#6366f1"}
                    onDelete={deleteEdge}
                  />
                );
              })}

              {wire && (() => {
                const fn = nodes.find(n => n.id === wire.fromId);
                if (!fn) return null;
                const op  = outPt(fn);
                const cmx = (op.x + mouse.x) / 2;
                return (
                  <path
                    d={`M${op.x},${op.y} C${cmx},${op.y} ${cmx},${mouse.y} ${mouse.x},${mouse.y}`}
                    fill="none" stroke="#6366f1" strokeWidth="2"
                    strokeDasharray="8 4" opacity=".4"
                    style={{ pointerEvents: "none" }}
                  />
                );
              })()}
            </g>
          </svg>
        </div>

        {/* Output strip */}
        {showOutput && (outputSteps.length > 0 || modelOutputImage) && (
          <div className="border-t flex-shrink-0"
            style={{ background: "#ffffff", borderColor: "#e5e7eb", maxHeight: 210 }}>
            <div className="flex items-center justify-between px-4 py-2 border-b" style={{ borderColor: "#e5e7eb" }}>
              <span className="text-xs font-bold flex items-center gap-2" style={{ color: "#111827" }}>
                <Layers size={13} style={{ color: "#6366f1" }} />
                Output — {outputSteps.length} step{outputSteps.length !== 1 ? "s" : ""}
              </span>
              <button onClick={() => setShowOutput(false)} style={{ color: "#9ca3af" }}
                onMouseEnter={e => e.currentTarget.style.color = "#111827"}
                onMouseLeave={e => e.currentTarget.style.color = "#9ca3af"}>
                <X size={14} />
              </button>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 overflow-x-auto">
              {outputSteps.map((step, i) => {
                return <div key={i} className="flex-shrink-0 text-center">
                  <div onClick={() => setPreviewSrc(step.base64)}
                    className="relative group w-28 h-20 rounded-xl overflow-hidden cursor-pointer"
                    style={{ border: "1px solid #e5e7eb" }}>
                    <img src={step.base64} className="w-full h-full object-cover" alt={`step-${i}`} />
                    <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Eye size={14} className="text-white" />
                    </div>
                  </div>
                  <p className="text-[9px] mt-1 font-mono truncate w-28" style={{ color: "#9ca3af" }}>
                    {step.label || `step_${i + 1}`}
                  </p>
                </div>;
              })}
              {modelOutputImage && (
                <div className="flex-shrink-0 text-center">
                  <div onClick={() => setPreviewSrc(modelOutputImage)}
                    className="relative group w-28 h-20 rounded-xl overflow-hidden cursor-pointer"
                    style={{ border: "2px solid #16a34a", boxShadow: "0 0 0 2px #16a34a20" }}>
                    <img src={modelOutputImage} className="w-full h-full object-cover" alt="model-output" />
                    <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Eye size={14} className="text-white" />
                    </div>
                  </div>
                  <p className="text-[9px] mt-1 font-mono truncate w-28" style={{ color: "#16a34a" }}>Model Output</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <Modal
        open={!!previewSrc}
        footer={null}
        centered
        width={740}
        onCancel={() => { setPreviewSrc(null); setConfirmNodeId(null); }}
        styles={{
          content: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 20, padding: 0, boxShadow: "0 20px 60px rgba(0,0,0,.15)" },
          body: { padding: 0 },
          mask: { backdropFilter: "blur(4px)", background: "rgba(0,0,0,0.25)" },
        }}
        closeIcon={<X size={15} style={{ color: "#9ca3af" }} />}
      >
        <div className="p-5 flex justify-center">
          <img src={previewSrc} alt="preview"
            className="max-h-[72vh] max-w-full object-contain rounded-2xl"
            style={{ border: "1px solid #e5e7eb" }} />
        </div>
        {confirmNodeId && (
          <div className="flex justify-end gap-2 px-5 pb-5">
            <button
              onClick={() => { setPreviewSrc(null); setConfirmNodeId(null); captureFrame(confirmNodeId); }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
              style={{ background: "#f9fafb", border: "1px solid #e5e7eb", color: "#6b7280" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
              onMouseLeave={e => e.currentTarget.style.background = "#f9fafb"}>
              <RefreshCw size={13} /> Recapture
            </button>
            <button
              onClick={() => { setPreviewSrc(null); setConfirmNodeId(null); }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
              style={{ background: "linear-gradient(135deg,#059669,#16a34a)", boxShadow: "0 2px 10px #05966930" }}>
              <Check size={13} /> Confirm Frame
            </button>
          </div>
        )}
      </Modal>

      {/* Multi-Camera Save Modal */}
      <Modal
        open={showCameraModal}
        footer={null}
        centered
        width={480}
        onCancel={() => setShowCameraModal(false)}
        styles={{
          content: {
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: 20,
            padding: 0,
            boxShadow: "0 20px 60px rgba(0,0,0,.15)",
          },
          body: { padding: 0 },
          mask: { backdropFilter: "blur(4px)", background: "rgba(0,0,0,0.25)" },
        }}
        closeIcon={<X size={15} style={{ color: "#9ca3af" }} />}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg,#059669,#16a34a)" }}>
              <Camera size={16} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-sm" style={{ color: "#111827" }}>Save to Other Cameras?</p>
              <p className="text-xs mt-0.5" style={{ color: "#6b7280" }}>
                You have {cameras.filter(c => c.channel !== nodes.find(n => n.type === "camera")?.cameraId).length} other camera{cameras.length > 2 ? "s" : ""} in this project
              </p>
            </div>
          </div>

          {/* Camera list */}
          <div className="rounded-xl p-3 mb-4 space-y-2"
            style={{ background: "#f8fafc", border: "1px solid #e5e7eb" }}>
            {/* Selected camera */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
              style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: "#16a34a" }} />
              <span className="text-xs font-semibold flex-1" style={{ color: "#16a34a" }}>
                {(() => {
                  const camNode = nodes.find(n => n.type === "camera" && n.cameraId);
                  const cam = cameras.find(c => c.channel === camNode?.cameraId);
                  return cam ? `${cam.cameraName} — Ch ${cam.channel}` : "Selected Camera";
                })()}
              </span>
              <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase"
                style={{ background: "#16a34a", color: "white" }}>Current</span>
            </div>

            {/* Other cameras */}
            {cameras
              .filter(c => c.channel !== nodes.find(n => n.type === "camera")?.cameraId)
              .map(c => (
                <div key={c.channel} className="flex items-center gap-2 px-3 py-2 rounded-lg"
                  style={{ background: "#ffffff", border: "1px solid #e5e7eb" }}>
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: "#0284c7" }} />
                  <span className="text-xs flex-1" style={{ color: "#374151" }}>
                    {c.cameraName} — Ch {c.channel}
                  </span>
                </div>
              ))}
          </div>

          <p className="text-xs mb-5" style={{ color: "#6b7280" }}>
            Would you like to apply this same pipeline configuration to all cameras, or only save for the currently selected camera?
          </p>

          {/* Buttons */}
          <div className="flex gap-2">
            {/* Only current */}
            <button
              onClick={() => {
                const camNode = nodes.find(n => n.type === "camera" && n.cameraId);
                savePipeline([camNode.cameraId]);
              }}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all"
              style={{ background: "#f9fafb", border: "1px solid #e5e7eb", color: "#374151", cursor: saving ? "not-allowed" : "pointer" }}
              onMouseEnter={e => { if (!saving) e.currentTarget.style.background = "#f3f4f6"; }}
              onMouseLeave={e => { if (!saving) e.currentTarget.style.background = "#f9fafb"; }}
            >
              <Camera size={13} />
              Current Only
            </button>

            {/* All cameras */}
            <button
              onClick={() => {
                const allIds = cameras.map(c => c.channel);
                savePipeline(allIds);
              }}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
              style={{
                background: saving ? "#d1d5db" : "linear-gradient(135deg,#059669,#16a34a)",
                boxShadow: saving ? "none" : "0 2px 10px #05966930",
                border: "none",
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              {saving
                ? <><RefreshCw size={12} className="animate-spin" /> Saving…</>
                : <><CheckCircle2 size={13} /> All Cameras</>
              }
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}